import { useNavigation } from "@react-navigation/native";
import { useTheme } from "arkh";
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { Platform } from "react-native";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { HeaderButton, HeaderButtonLoading } from "../../../components";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { PLUProductComponent } from "../PLUProductComponent/PLUProductComponent";
import { validPLUProduct } from "./validPLUProduct";
import { PLUProductEditScreenDeleteMutation } from "./__generated__/PLUProductEditScreenDeleteMutation.graphql";
import { PLUProductEditScreenQuery } from "./__generated__/PLUProductEditScreenQuery.graphql";
import { PLUProductEditScreenUpdateMutation } from "./__generated__/PLUProductEditScreenUpdateMutation.graphql";
import { captureException } from "../../../common/sentry";
import { PLUProductEditScreenProduct_PLUProduct$key } from "./__generated__/PLUProductEditScreenProduct_PLUProduct.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { useAlert } from "../../../common/Alert";
type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"PLUItemEdit">;
  route: UserLoggedInNavigatorRouteProp<"PLUItemEdit">;
};

export const PLUProductEditScreen = withWebModal(PLUProductEditWithStack);

type ParamList = {
  PLUProductEditScreenMain: { id: string };
};
const Stack = createNativeStackNavigator<ParamList>();

function PLUProductEditWithStack(props: Props) {
  const theme = useTheme();
  return (
    <Stack.Navigator>
      <Stack.Screen
        component={PLUProductEditScreenComponent}
        name="PLUProductEditScreenMain"
        options={({ navigation }) => ({
          headerShown: true,
          title: "",
          headerStyle: {
            backgroundColor: theme.background,
          },
          headerShadowVisible: false,
          headerLeft:
            Platform.OS === "ios"
              ? () => (
                  <HeaderButton
                    type="CANCEL"
                    onPress={() => {
                      navigation.goBack();
                    }}
                  />
                )
              : undefined,
        })}
        initialParams={{ id: props.route.params.id }}
      />
    </Stack.Navigator>
  );
}
function PLUProductEditScreenComponent(props: {
  route: {
    params: { id: string };
  };
}) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerPLUProductEditScreen
          fetchKey={fetchKey}
          id={props.route.params.id}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerPLUProductEditScreen(props: { fetchKey: string; id: string }) {
  const data = useLazyLoadQuery<PLUProductEditScreenQuery>(
    graphql`
      query PLUProductEditScreenQuery($id: ID!) {
        pluProduct(id: $id) {
          ...PLUProductEditScreenProduct_PLUProduct
        }
        pluCategories {
          id
          color
          title
        }
      }
    `,
    {
      id: props.id,
    },
    { fetchKey: props.fetchKey }
  );

  return data.pluProduct ? (
    <Component product={data.pluProduct} pluCategories={data.pluCategories} />
  ) : null;
}

function Component(props: {
  pluCategories: readonly { id: string; color: string; title: string }[];
  product: PLUProductEditScreenProduct_PLUProduct$key;
}) {
  const remoteProduct = useFragment(
    graphql`
      fragment PLUProductEditScreenProduct_PLUProduct on PLUProduct {
        id
        title
        active
        eanCode
        eanType
        pluCode
        category {
          id
        }
        image {
          id
          ...Image_image
        }
      }
    `,
    props.product
  );
  const theme = useTheme();
  const navigation =
    useNavigation<UserLoggedInNavigatorNavigationProp<"PLUItemEdit">>();
  const [active, setActive] = useState(remoteProduct.active);

  const [title, setTitle] = useState(remoteProduct.title);
  const [eanCode, setEanCode] = useState(remoteProduct.eanCode);
  const [eanType, setEanType] = useState<string>(remoteProduct.eanType);
  const [pluCode, setPluCode] = useState(remoteProduct.pluCode);
  const [categoryId, setCategoryId] = useState(remoteProduct.category.id);

  const [newImage, setNewImage] = useState<{ id: string; uri: string } | null>(
    null
  );
  const selectedCategory = useMemo(
    () =>
      props.pluCategories.find((category) => category.id === categoryId) ??
      null,
    [categoryId, props.pluCategories]
  );

  const [updateProduct, isUpdating] =
    useMutation<PLUProductEditScreenUpdateMutation>(graphql`
      mutation PLUProductEditScreenUpdateMutation(
        $categoryId: ID!
        $id: ID!
        $input: PLUUpdateProductInput!
      ) {
        pluCategory(id: $categoryId) {
          updateProduct(id: $id, input: $input) {
            pluProduct {
              id
              title
              eanCode
              pluCode
              image {
                id
                ...Image_image
              }
              category {
                id
                products {
                  ...PLUProductItem_PLUProduct
                }
              }
            }
            previousPLUCategory {
              id
              products {
                ...PLUProductItem_PLUProduct
              }
            }
          }
        }
      }
    `);

  const [deleteProduct, isDeleting] =
    useMutation<PLUProductEditScreenDeleteMutation>(graphql`
      mutation PLUProductEditScreenDeleteMutation($categoryId: ID!, $id: ID!) {
        pluCategory(id: $categoryId) {
          deleteProduct(id: $id) {
            id
            category {
              products {
                id
              }
            }
          }
        }
      }
    `);

  const Alert = useAlert();
  const onSave = useCallback(() => {
    updateProduct({
      variables: {
        categoryId: remoteProduct.category.id,
        id: remoteProduct.id,
        input: {
          setCategoryId:
            categoryId !== remoteProduct.category.id ? categoryId : undefined,
          setEanCode: eanCode !== remoteProduct.eanCode ? eanCode : undefined,
          setEanType:
            eanType !== remoteProduct.eanType
              ? eanType === "CODE39"
                ? "CODE39"
                : eanType === "CODE128"
                  ? "CODE128"
                  : "EAN"
              : undefined,
          deleteEanCode: remoteProduct.eanCode !== null && eanCode === "",
          deletePluCode: remoteProduct.pluCode !== null && pluCode === "",
          setPluCode: pluCode !== remoteProduct.pluCode ? pluCode : undefined,
          setImageId: newImage !== null ? newImage.id : undefined,
          setTitle: title !== remoteProduct.title ? title : undefined,
          setActive: active !== remoteProduct.active ? active : undefined,
        },
      },
      onCompleted: () => {
        navigation.goBack();
      },

      onError: (e) => {
        console.log("update product error", e);
        captureException(e);
        Alert.alert("Error", "Failed to update product");
      },
    });
  }, [
    Alert,
    active,
    categoryId,
    eanCode,
    eanType,
    navigation,
    newImage,
    pluCode,
    remoteProduct.active,
    remoteProduct.category.id,
    remoteProduct.eanCode,
    remoteProduct.eanType,
    remoteProduct.id,
    remoteProduct.pluCode,
    remoteProduct.title,
    title,
    updateProduct,
  ]);
  const onDelete = useCallback(() => {
    deleteProduct({
      variables: {
        categoryId: remoteProduct.category.id,
        id: remoteProduct.id,
      },
      onCompleted: () => {
        navigation.goBack();
      },
    });
  }, [deleteProduct, navigation, remoteProduct.category.id, remoteProduct.id]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () =>
        isUpdating ? (
          <HeaderButtonLoading color={theme.primary} />
        ) : (
          <HeaderButton
            type="SAVE"
            onPress={onSave}
            disabled={
              !validPLUProduct({
                title,
                eanCode,
                eanType,
                categoryId,
              })
            }
          />
        ),
    });
  }, [
    categoryId,
    eanCode,
    eanType,
    isUpdating,
    navigation,
    onSave,
    pluCode,
    theme.primary,
    title,
  ]);

  return (
    <PLUProductComponent
      active={active}
      setActive={setActive}
      image={
        newImage
          ? { type: "local", uri: newImage.uri }
          : remoteProduct.image
            ? {
                type: "remote",
                image: remoteProduct.image,
              }
            : null
      }
      setImage={setNewImage}
      title={title}
      setTitle={setTitle}
      eanCode={eanCode ?? ""}
      setEanCode={(code, type) => {
        setEanCode(code);
        setEanType(type);
      }}
      pluCode={pluCode ?? ""}
      setPluCode={setPluCode}
      setSelectedCategory={setCategoryId}
      categories={props.pluCategories}
      selectedCategory={selectedCategory}
      onDelete={onDelete}
      isDeleting={isDeleting}
    />
  );
}
